import { Extension } from '@tiptap/react'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { router } from '@inertiajs/react'

const wikiLinkHighlightKey = new PluginKey('wikiLinkHighlight')
const WIKI_LINK_RE = /\[\[([^\]]+)\]\]/g

export interface WikiLinkHighlightOptions {
  workspaceSlug?: string
}

export const WikiLinkHighlight = Extension.create<WikiLinkHighlightOptions>({
  name: 'wikiLinkHighlight',

  addOptions() {
    return {
      workspaceSlug: undefined,
    }
  },

  addProseMirrorPlugins() {
    const workspaceSlug = this.options.workspaceSlug

    return [
      new Plugin({
        key: wikiLinkHighlightKey,
        props: {
          decorations(state) {
            const decorations: Decoration[] = []
            const { doc, selection } = state

            // Determine active block text range
            const $head = selection.$head
            const depth = $head.depth
            const activeFrom = depth > 0 ? $head.start(depth) : 0
            const activeTo = depth > 0 ? $head.end(depth) : 0

            doc.descendants((node, pos) => {
              if (!node.isText || !node.text) return

              const inActiveBlock = pos >= activeFrom && pos <= activeTo

              let match: RegExpExecArray | null
              WIKI_LINK_RE.lastIndex = 0
              while ((match = WIKI_LINK_RE.exec(node.text)) !== null) {
                const start = pos + match.index
                const end = start + match[0].length
                const innerText = match[1]
                const pipeIdx = innerText.indexOf('|')
                const linkPart = (pipeIdx >= 0 ? innerText.slice(0, pipeIdx) : innerText).trim()
                const pageTitle = linkPart.split('/').pop()!.trim()

                if (inActiveBlock) {
                  // Active block: show full [[Title]] with subtle styling
                  decorations.push(
                    Decoration.inline(start, end, {
                      class: 'wiki-link wiki-link-editing',
                      'data-wiki-title': pageTitle,
                    })
                  )
                } else {
                  // Inactive block: hide everything except display text
                  const displayOffset = pipeIdx >= 0 ? pipeIdx + 1 : 0
                  const displayStart = start + 2 + displayOffset
                  const displayEnd = end - 2

                  // Hide prefix: [[ and optionally "Title|"
                  if (start < displayStart) {
                    decorations.push(
                      Decoration.inline(start, displayStart, {
                        class: 'wiki-link-hidden',
                      })
                    )
                  }
                  // Style display text
                  decorations.push(
                    Decoration.inline(displayStart, displayEnd, {
                      class: 'wiki-link',
                      'data-wiki-title': pageTitle,
                      ...(workspaceSlug ? { 'data-workspace-slug': workspaceSlug } : {}),
                    })
                  )
                  // Hide suffix ]]
                  decorations.push(
                    Decoration.inline(displayEnd, end, {
                      class: 'wiki-link-hidden',
                    })
                  )
                }
              }
            })

            return DecorationSet.create(doc, decorations)
          },

          // Use mousedown — fires BEFORE ProseMirror updates the selection,
          // so decorations in the DOM still reflect the previous active block.
          handleDOMEvents: {
            mousedown(view, event) {
              if (event.button !== 0 || event.shiftKey || event.altKey) return false

              const target = event.target as HTMLElement

              const wikiLink = target.closest('.wiki-link:not(.wiki-link-editing)') as HTMLElement | null
              const regularLink = !wikiLink ? target.closest('a[href]') as HTMLAnchorElement | null : null

              if (!wikiLink && !regularLink) return false

              // Check if click is in the currently active block
              const clickPos = view.posAtCoords({ left: event.clientX, top: event.clientY })
              if (!clickPos) return false

              const $head = view.state.selection.$head
              const depth = $head.depth
              const activeFrom = depth > 0 ? $head.start(depth) : -1
              const activeTo = depth > 0 ? $head.end(depth) : -1
              const isInActiveBlock = clickPos.pos >= activeFrom && clickPos.pos <= activeTo

              if (isInActiveBlock) return false // let editor handle cursor

              event.preventDefault()

              if (wikiLink && workspaceSlug) {
                const pageTitle = wikiLink.dataset.wikiTitle
                if (!pageTitle) return false

                fetch(`/api/${workspaceSlug}/pages/search?q=${encodeURIComponent(pageTitle)}`)
                  .then(res => res.json())
                  .then(pages => {
                    const page = pages.find((p: { title: string }) => p.title === pageTitle)
                    if (page) {
                      router.visit(`/${workspaceSlug}/${page.path}`)
                    }
                  })
                  .catch(() => {})
                return true
              }

              if (regularLink) {
                const href = regularLink.getAttribute('href')
                if (href) {
                  window.open(href, '_blank', 'noopener,noreferrer')
                  return true
                }
              }

              return false
            },
          },
        },
      }),
    ]
  },
})
